import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Eye, Loader2, ChevronLeft, ChevronRight, Info, ClipboardCheck,
  AlertTriangle, CheckCircle2, Package, Hammer
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { Dialog } from '@components/Dialog';
import { inventorySessionService } from '../../services/inventorySessionService';
import { InventorySession } from '../../types/inventorySession';
import { clsx } from 'clsx';

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

export const InventorySessionListView: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<InventorySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  // 0 = Produtos (default) | 1 = Matérias-Primas
  const [newSessionType, setNewSessionType] = useState(0);
  const [creating, setCreating] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<InventorySession | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await inventorySessionService.getSessions({
        page: currentPage,
        pageSize,
        orderBy: 'created_at',
        orderDirection: 'desc',
      });
      setSessions(result.data);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotalItems(result.pagination?.total || 0);
    } catch (err) {
      setErrorMessage('Erro ao carregar sessões de inventário');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const defaultTitleForType = (sessionType: number) => {
    const today = new Date().toLocaleDateString('pt-BR');
    return sessionType === 1
      ? `Inventário de Matérias-Primas de ${today}`
      : `Inventário de ${today}`;
  };

  const handleOpenCreate = () => {
    setNewSessionType(0);
    setNewTitle(defaultTitleForType(0));
    setCreateDialogOpen(true);
  };

  const handleChangeSessionType = (sessionType: number) => {
    setNewSessionType(sessionType);
    setNewTitle(defaultTitleForType(sessionType));
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const session = await inventorySessionService.createSession({
        title: newTitle.trim(),
        session_type: newSessionType,
      });
      setCreateDialogOpen(false);
      setSuccessMessage('Inventário criado com sucesso!');
      setTimeout(() => setSuccessMessage(''), 4000);
      // Navigate to the detail page
      navigate(`/stock-movements/inventory/${session.inventory_session_id}`);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Erro ao criar inventário');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setCreating(false);
    }
  };

  const handleOpenDelete = (session: InventorySession) => {
    setSessionToDelete(session);
    setDeleteError('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!sessionToDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await inventorySessionService.deleteSession(sessionToDelete.inventory_session_id);
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
      setSuccessMessage('Inventário excluído com sucesso!');
      setTimeout(() => setSuccessMessage(''), 4000);
      loadSessions();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Erro ao excluir inventário');
    } finally {
      setDeleting(false);
    }
  };

  const getProgressPercentage = (session: InventorySession) => {
    if (session.total_entries === 0) return 0;
    return Math.round((session.conferred_entries / session.total_entries) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Success message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Info size={18} /> {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle size={18} /> {errorMessage}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end">
        <PermissionGuard permission="stock_movements.create">
          <Button icon={<Plus size={18} />} onClick={handleOpenCreate}>
            Novo Inventário
          </Button>
        </PermissionGuard>
      </div>

      {/* Sessions list */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">ID</th>
                  <th className="text-left">Título</th>
                  <th className="text-center">Tipo</th>
                  <th className="text-left">Criado por</th>
                  <th className="text-left">Data de Criação</th>
                  <th className="text-center">Progresso</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 size={24} className="animate-spin text-emerald-500" />
                        <span className="text-sm text-slate-500">Carregando inventários...</span>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && sessions.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-12">
                      <div className="flex flex-col items-center">
                        <div className="text-5xl text-slate-200 mb-3">📋</div>
                        <h3 className="text-base font-medium text-slate-700 mb-1">Nenhum inventário encontrado</h3>
                        <p className="text-sm text-slate-500">Crie um novo inventário para começar a conferência de estoque</p>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && sessions.map(session => {
                  const progress = getProgressPercentage(session);
                  return (
                    <TableRow key={session.inventory_session_id}>
                      <TableCell className="font-mono text-sm">#{session.inventory_session_id}</TableCell>
                      <TableCell className="font-medium">{session.title}</TableCell>
                      <TableCell className="text-center">
                        {session.session_type === 1 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-purple-100 text-purple-700 border-purple-200 whitespace-nowrap">
                            <Hammer size={12} /> Matérias-Primas
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-slate-100 text-slate-600 border-slate-200 whitespace-nowrap">
                            <Package size={12} /> Produtos
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{session.created_by || '—'}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{formatDate(session.created_at)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-24 bg-slate-200 rounded-full h-2">
                            <div
                              className={clsx(
                                'h-2 rounded-full transition-all',
                                progress === 100 ? 'bg-emerald-500' : 'bg-blue-500'
                              )}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            {session.conferred_entries}/{session.total_entries}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {session.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-blue-100 text-blue-700 border-blue-200">
                            <ClipboardCheck size={12} /> Em andamento
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-emerald-100 text-emerald-700 border-emerald-200">
                            <CheckCircle2 size={12} /> Finalizado
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600 hover:text-emerald-600 transition-colors"
                            title="Visualizar inventário"
                            onClick={() => navigate(`/stock-movements/inventory/${session.inventory_session_id}`)}
                          >
                            <Eye size={16} />
                          </button>
                          {session.is_active && !session.has_conferred_entries && (
                            <PermissionGuard permission="stock_movements.create">
                              <button
                                className="p-1.5 rounded-md hover:bg-red-50 text-slate-600 hover:text-red-600 transition-colors"
                                title="Excluir inventário"
                                onClick={() => handleOpenDelete(session)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </PermissionGuard>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                Página {currentPage} de {totalPages} ({totalItems} registros)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  icon={<ChevronLeft size={16} />}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  icon={<ChevronRight size={16} />}
                  iconPosition="right"
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        isOpen={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        title="Novo Inventário de Estoque"
        size="md"
        closeOnBackdrop={!creating}
        showCloseButton={!creating}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={creating}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleCreate} loading={creating} disabled={!newTitle.trim()}>
              Criar Inventário
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Tipo de Inventário <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <label className={clsx(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                newSessionType === 0 ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
              )}>
                <input
                  type="radio"
                  name="session_type"
                  checked={newSessionType === 0}
                  onChange={() => handleChangeSessionType(0)}
                  className="text-emerald-600 focus:ring-emerald-500"
                  disabled={creating}
                />
                <div className="flex items-center gap-2">
                  <Package size={16} className="text-slate-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">Produtos</p>
                    <p className="text-xs text-slate-500">Conferência dos produtos ativos, agrupados por fornecedor</p>
                  </div>
                </div>
              </label>
              <label className={clsx(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                newSessionType === 1 ? 'border-purple-300 bg-purple-50' : 'border-slate-200 hover:bg-slate-50'
              )}>
                <input
                  type="radio"
                  name="session_type"
                  checked={newSessionType === 1}
                  onChange={() => handleChangeSessionType(1)}
                  className="text-purple-600 focus:ring-purple-500"
                  disabled={creating}
                />
                <div className="flex items-center gap-2">
                  <Hammer size={16} className="text-slate-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">Matérias-Primas</p>
                    <p className="text-xs text-slate-500">Conferência de matérias-primas, insumos, compostos e semiacabados</p>
                  </div>
                </div>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Título do Inventário <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: Inventário de 11/05/2026"
              maxLength={100}
              disabled={creating}
            />
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
            <p className="font-medium mb-1">ℹ️ Informação:</p>
            <ul className="list-disc list-inside space-y-0.5">
              {newSessionType === 1 ? (
                <>
                  <li>Ao criar o inventário, todas as matérias-primas ativas serão incluídas automaticamente</li>
                  <li>Os itens serão agrupados por tipo de material (Matérias-Primas, Insumos, Compostos e Semiacabados)</li>
                </>
              ) : (
                <>
                  <li>Ao criar o inventário, todos os produtos ativos serão incluídos automaticamente</li>
                  <li>Os produtos serão agrupados por fornecedor para facilitar a conferência</li>
                </>
              )}
              <li>O inventário será finalizado automaticamente quando todos os itens forem conferidos</li>
            </ul>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        isOpen={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        title="Excluir Inventário"
        size="sm"
        closeOnBackdrop={!deleting}
        showCloseButton={!deleting}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              Excluir
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Tem certeza que deseja excluir o inventário <strong>{sessionToDelete?.title}</strong>?
          </p>
          <p className="text-xs text-slate-500">
            Esta ação não pode ser desfeita. Todos os registros de conferência serão removidos.
          </p>
          {deleteError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {deleteError}
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};
